import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const VietnameseCommunityHomesOCPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Vietnamese community properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Vietnamese Community Homes Orange County | Truc Doan Realtor</title>
        <meta name="description" content="Find homes in Orange County's Vietnamese communities – Westminster, Garden Grove, Santa Ana, and Fountain Valley. Truc Doan is your bilingual Vietnamese-American realtor specializing in Little Saigon and surrounding areas. Homes from $650K-$900K." />
        <meta name="keywords" content="Vietnamese community homes Orange County, Little Saigon homes for sale, Westminster Vietnamese real estate, Garden Grove Vietnamese community, Santa Ana Vietnamese homes, Fountain Valley homes, Vietnamese realtor Orange County, bilingual realtor, Truc Doan, OC Vietnamese neighborhoods" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/vietnamese-community-homes-orange-county`} />
        <meta property="og:title" content="Vietnamese Community Homes Orange County | Truc Doan Realtor" />
        <meta property="og:description" content="Homes in OC's Vietnamese communities – Westminster, Garden Grove, Santa Ana, Fountain Valley. Bilingual agent Truc Doan. $650K-$900K range." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/vietnamese-community-homes-orange-county`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Orange County, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/vietnamese-community-homes-orange-county`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a bilingual Vietnamese-American realtor specializing in homes within Orange County's Vietnamese communities including Westminster, Garden Grove, Santa Ana, and Fountain Valley.",
            "areaServed": {
              "@type": "AdministrativeArea",
              "name": "Orange County, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            },
            "knowsLanguage": ["English", "Vietnamese"]
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Vietnamese Community Homes in Orange County</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Find your home in OC's vibrant Vietnamese neighborhoods – from Little Saigon to Garden Grove and beyond. Bilingual service with Truc Doan.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Orange County's Vietnamese Communities</h2>
          <p>Orange County is home to the largest Vietnamese-American population outside of Vietnam. From the bustling streets of Little Saigon in Westminster to the family-friendly neighborhoods of Garden Grove and Fountain Valley, these communities offer rich cultural experiences, authentic cuisine, and a strong sense of belonging.</p>
          <p>As a Vietnamese-American realtor, Truc Doan understands the unique needs of families seeking homes near Vietnamese cultural centers, temples, markets, and community organizations. Whether you're a first-time buyer or relocating to be closer to family, Truc provides bilingual guidance every step of the way.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Vietnamese Neighborhoods Across Orange County</h2>
          <p>Explore the diverse Vietnamese communities throughout OC, each offering unique character and amenities.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Westminster / Little Saigon</h3>
              <p>The cultural heart of Vietnamese-American life in OC. Home to Bolsa Avenue's restaurants, Asian Garden Mall, Phước Lộc Thọ, and hundreds of Vietnamese businesses. Median homes $650K-$800K.</p>
            </div>
            <div className="amenity">
              <h3>Garden Grove</h3>
              <p>Family-friendly neighborhoods with excellent schools, Vietnamese supermarkets, temples, and community centers. Affordable homes ranging $700K-$850K with larger lot sizes.</p>
            </div>
            <div className="amenity">
              <h3>Santa Ana</h3>
              <p>Growing Vietnamese community with affordable housing options, proximity to downtown OC, and diverse cultural offerings. Entry-level homes from $600K-$750K.</p>
            </div>
            <div className="amenity">
              <h3>Fountain Valley</h3>
              <p>Quiet, suburban community adjacent to Westminster with top-rated schools, parks, and easy access to Little Saigon amenities. Homes $800K-$950K.</p>
            </div>
            <div className="amenity">
              <h3>Cultural Centers & Temples</h3>
              <p>Buddhist temples, community organizations, Vietnamese language schools, and cultural centers throughout OC keep traditions alive for families across generations.</p>
            </div>
            <div className="amenity">
              <h3>Markets & Dining</h3>
              <p>From Lee's Sandwiches to pho restaurants, bánh mì shops, and full-service Asian supermarkets – every neighborhood offers authentic Vietnamese food and shopping within minutes.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Vietnamese Community Areas Market Snapshot</h2>
          <p>Homes in OC's Vietnamese communities offer excellent value compared to the broader Orange County market while providing unmatched cultural amenities.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>OC Median Home Price</td><td>~$1,050,000</td></tr>
              <tr><td>Vietnamese Community Areas</td><td>$650K–$900K</td></tr>
              <tr><td>Westminster Median</td><td>~$750,000</td></tr>
              <tr><td>Garden Grove Median</td><td>~$800,000</td></tr>
              <tr><td>Most Active Price Range</td><td>$600K – $850K</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Homes for Sale in Vietnamese Communities</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all available Orange County listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Find Your Home in OC's Vietnamese Community</h2>
          <p>Contact Truc Doan today for bilingual real estate assistance. Whether you speak Vietnamese or English, Truc is here to help you find the perfect home.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Vietnamese Community Homes Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default VietnameseCommunityHomesOCPage;
